import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { SERVICE_CATALOG } from "../lib/serviceCatalog";
import { BRAND } from "../lib/constants";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import Header from "../sections/Header";
import Footer from "../sections/Footer";
import StickyCTA from "../sections/StickyCTA";
import * as Icons from "lucide-react";
import { ArrowLeft, CheckCircle2, Phone, ArrowRight } from "lucide-react";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = SERVICE_CATALOG[slug];

  useEffect(() => {
    if (!service) return;
    document.title = `${service.title} | Madison Peaceful Path Home Care | Madison, WI`;
    const setMeta = (name, content, property = false) => {
      const attr = property ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", service.metaDescription);
    setMeta("og:title", `${service.title} in Madison, WI`, true);
    setMeta("og:description", service.metaDescription, true);
  }, [service]);

  if (!service) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center">
          <p className="font-serif text-3xl text-[#005165] mb-3">Service not found</p>
          <Link to="/" className="text-[#AC9667] underline">← Back home</Link>
        </div>
      </div>
    );
  }

  const Icon = Icons[service.icon] || Icons.Heart;

  return (
    <div data-testid={`service-detail-${slug}`} className="min-h-screen bg-white">
      <Header />

      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-br from-[#005165] to-[#003946] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 80% 20%, #AC9667 0%, transparent 40%)'
        }} aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#C4B088] mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to services
          </button>
          <div className="flex items-start gap-5">
            <div className="w-16 h-16 rounded-2xl bg-[#AC9667]/20 border border-[#AC9667]/40 flex items-center justify-center shrink-0">
              <Icon className="w-8 h-8 text-[#C4B088]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#C4B088] mb-2">Service · Madison, WI</p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05]">{service.title}</h1>
              <p className="mt-5 text-lg text-white/80 max-w-2xl leading-relaxed">{service.short}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/#contact">
                  <Button data-testid="svc-cta-consult" className="bg-[#AC9667] hover:bg-[#7A6A48] text-white rounded-full h-12 px-6">
                    Schedule Free Consultation <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href={`tel:${BRAND.phoneRaw}`}>
                  <Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-[#005165] rounded-full h-12 px-6">
                    <Phone className="w-4 h-4 mr-2" /> {BRAND.phone}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-serif text-3xl md:text-4xl text-[#005165] mb-5">Overview</h2>
            <p className="text-[17px] text-[#4B5563] leading-relaxed">{service.overview}</p>

            <h2 className="font-serif text-3xl md:text-4xl text-[#005165] mt-12 mb-5">Benefits</h2>
            <ul className="space-y-3">
              {service.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[16px] text-[#1F2937]">
                  <CheckCircle2 className="w-5 h-5 text-[#AC9667] shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <h2 className="font-serif text-3xl md:text-4xl text-[#005165] mt-12 mb-5">Questions families ask</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {service.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} className="bg-[#F8F9FA] rounded-xl border border-[#E5DDC9] px-5">
                  <AccordionTrigger className="text-left font-serif text-lg text-[#005165]">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[#4B5563]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-28 bg-gradient-to-br from-[#005165] to-[#003946] text-white rounded-3xl p-7">
              <p className="text-xs uppercase tracking-[0.22em] text-[#C4B088] mb-3">Get started</p>
              <p className="font-serif text-2xl leading-tight mb-4">
                Free in-home consultation. No pressure, no obligation.
              </p>
              <Link to="/#contact">
                <Button className="w-full bg-[#AC9667] hover:bg-[#7A6A48] text-white rounded-full h-12">
                  Schedule Consultation
                </Button>
              </Link>
              <a href={`tel:${BRAND.phoneRaw}`} className="block mt-3 text-center text-sm text-white/80 hover:text-[#C4B088]">
                Or call {BRAND.phone}
              </a>
              <div className="mt-6 pt-6 border-t border-white/10 space-y-2 text-sm text-white/70">
                <p>✓ Available 24/7</p>
                <p>✓ Background-checked caregivers</p>
                <p>✓ Local Madison team</p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
      <StickyCTA />
    </div>
  );
}
